import { PaginationState, SortingState } from '@tanstack/react-table';
import { z } from 'zod';

export const ImportTypeLobSchema = z.object({
	importTypeLobId: z.number().nullable().optional(),
	importTypeId: z.number(),
	compLocId: z.number(),
	brandName: z.string().nullable().optional(),
	lobId: z.number(),
	numInstallments: z.number().nullable().optional(),
	lobDescription: z.string().nullable().optional(),
	effectiveDate: z.preprocess(
		(val) => (val ? new Date(val as string | Date) : null),
		z.date().nullable().optional(),
	),
	expirationDate: z.preprocess(
		(val) => (val ? new Date(val as string | Date) : null),
		z.date().nullable().optional(),
	),
});

export type ImportTypeLob = z.infer<typeof ImportTypeLobSchema>;

export type SelectOption = {
	value: number;
	label: string;
};

export const all = async (
	pagination: PaginationState,
	sorting: SortingState = [],
	importTypeId: number,
	params: object,
): Promise<{ data: ImportTypeLob[]; total: number }> => {
	let queryString = '';
	if (params) {
		const paramsAsString = JSON.stringify(params)
			.replace(/:/g, '=')
			.replace(/,/g, '&')
			.replace(/[{}"]/g, '');

		const searchParams = new URLSearchParams(paramsAsString);
		if (pagination) {
			searchParams.append('pageSize', pagination.pageSize.toString());
			searchParams.append('pageIndex', pagination.pageIndex.toString());
		}

		if (sorting.length > 0) {
			searchParams.append('sortBy', sorting[0].id);
			searchParams.append('sortDirection', sorting[0].desc ? 'desc' : 'asc');
		}

		queryString = `?${searchParams.toString()}`;
	}

	const response = await fetch(
		`/admin/lob/${importTypeId}${queryString}`,
	);

	return await response.json();
};

export const add = async (
	lob: ImportTypeLob,
): Promise<boolean> => {
	const res = await fetch(`/admin/lob`, {
		method: 'POST',
		body: JSON.stringify(lob),
		headers: {
			'Content-Type': 'application/json',
		},
	});
	return res.ok;
};

export const update = async (
	lob: ImportTypeLob,
): Promise<boolean> => {
	const res = await fetch(`/admin/lob`, {
		method: 'PATCH',
		body: JSON.stringify(lob),
		headers: {
			'Content-Type': 'application/json',
		},
	});
	return res.ok;
};

export const getLobOptions = async (
	importTypeId: number,
    compLocId: number,
    selected?: number,
): Promise<SelectOption[]> => {
    const params = selected ? `?selected=${selected}` : '';
	const response = await fetch(
		`/admin/lob/${importTypeId}/options/${compLocId}${params}`,
	);
	return await response.json();
};
